import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * AiContentNotice — eticheta de transparenta pentru continut produs de modele AI.
 *
 * DE CE EXISTA: EU AI Act art. 50(1) (interactiune directa cu un sistem AI) si
 * art. 50(4) (text generat/manipulat de AI, publicat catre public), aplicabile
 * din 2 august 2026. Marcajul machine-readable din art. 50(2) e alt strat si nu
 * trece prin componenta asta.
 *
 * SURSA UNICA de formulare. Daca textul trebuie schimbat (audit, consultanta
 * juridica, ghid nou al Comisiei), se schimba intr-un singur loc: cheile
 * `app.ai_notice.*` din i18n. Nu scrie fraza direct in pagini.
 *
 * VARIANTE — alege dupa CE e continutul de sub eticheta, nu dupa cat de vizibil
 * vrei sa fie avertismentul:
 *   - verbatim    text reprodus literal dintr-un raspuns al unui LLM tert
 *                 (citate mini-scan, raspunsuri per intrebare, claim-uri)
 *   - generated   proza scrisa de un model in numele nostru (rezumat executiv,
 *                 recomandari, sumar de alerte)
 *   - derived     cifre/clasamente calculate determinist DIN raspunsuri AI
 *                 (RRI, scoruri, clasamente) — nu e text generat, dar sursa e AI
 *   - interactive art. 50(1): omul interactioneaza pe loc cu un sistem AI
 *
 * NU pune eticheta peste agregate care nu vin din AI (ex. RawObservationsCard,
 * care afiseaza statistici deterministe si spune deja explicit ca nu contine
 * claim-uri generate). O eticheta falsa dilueaza semnalul celorlalte.
 *
 * @param {'verbatim'|'generated'|'derived'|'interactive'} variant
 * @param {'inline'|'callout'|'chip'} tone   inline = micro-copy sub titlu;
 *        callout = banda vizibila pe pagini publice; chip = badge compact
 * @param {Object} style      suprascrieri punctuale (marginTop, maxWidth...)
 * @param {string} className  ex. 'pb-print-keep' cand nota intra intr-un PDF
 */

const KEYS = {
  verbatim: 'app.ai_notice.verbatim',
  generated: 'app.ai_notice.generated',
  derived: 'app.ai_notice.derived',
  interactive: 'app.ai_notice.interactive',
};

// Tonurile reproduc tipare vizuale care exista deja in produs, ca sa nu apara
// un al doilea limbaj de avertisment: `callout` = banda de nota din
// RriLeaderboardPage, `inline` = micro-copy-ul din MiniScanConsumerResult,
// `chip` = RatingChip. Culori solide, fara gradient alpha: gradientele alpha
// devin /SMask in PDF si blocheaza imprimantele ieftine (vezi @media print).
const TONES = {
  inline: {
    fontFamily: 'DM Mono, monospace',
    fontSize: 10.5,
    lineHeight: 1.6,
    color: 'var(--muted, #6B7280)',
    marginTop: 8,
  },
  callout: {
    padding: '10px 14px',
    background: '#FFFBEB',
    border: '1px solid rgba(212,175,55,0.4)',
    borderRadius: 12,
    fontFamily: 'DM Mono, monospace',
    fontSize: 11.5,
    lineHeight: 1.6,
    color: '#92400E',
    marginBottom: 24,
  },
  chip: {
    display: 'inline-block',
    fontFamily: 'DM Mono, monospace',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    padding: '3px 8px',
    borderRadius: 999,
    background: 'rgba(183,121,31,0.14)',
    color: '#B7791F',
  },
};

export default function AiContentNotice({
  variant = 'derived',
  tone = 'inline',
  style = null,
  className = '',
}) {
  const { t } = useTranslation();
  return (
    <div
      role="note"
      data-ai-notice={variant}
      className={className}
      style={{ ...(TONES[tone] || TONES.inline), ...(style || {}) }}
    >
      {t(KEYS[variant] || KEYS.derived)}
    </div>
  );
}
